import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Stacked label + input skeleton rows. Used as the loading state for
 * any form-shaped surface (connector configuration, connection settings,
 * oauth-app credentials, etc.) so the page's vertical rhythm doesn't
 * lurch when real fields render.
 *
 * Per DESIGN_GUIDELINES §10 ("Skeleton footprint discipline"): each
 * skeleton should reserve the dimensions the loaded UI will occupy.
 * Pass `rows` to match the rough field count of the form below.
 */
export function FormLoadingShell({
	rows = 4,
	className,
}: {
	/** Number of label + input pairs to render. Default 4. */
	rows?: number;
	className?: string;
}) {
	return (
		<div
			role="status"
			aria-label="Loading form"
			className={cn("space-y-5", className)}
		>
			{Array.from({ length: rows }).map((_, i) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton blocks
					key={i}
					className="space-y-2"
				>
					{/* Label */}
					<Skeleton className="h-3.5 w-24" />
					{/* Input field */}
					<Skeleton className="h-9 w-full rounded-md" />
				</div>
			))}
			{/* Trailing action row (Save / Cancel pair) */}
			<div className="flex items-center gap-2 pt-2">
				<Skeleton className="h-9 w-24 rounded-md" />
				<Skeleton className="h-9 w-20 rounded-md" />
			</div>
		</div>
	);
}
